// Validation rules. Each rule is a pure function:
//   (context) → Issue[]
// where Issue = { id, severity: 'error'|'warning', ruleId, elementId, message }
//
// The shared `context` is built by Validator from the moddle definitions.

import { parse } from '../feel/Parser';
import { compileUnaryTests } from '../feel/UnaryTest';
import { BUILTIN_ALIASES } from '../feel/builtins';
import { sanitizeFeelIdent } from '../feel/ident';

let uid = 0;
const mkIssue = (severity, ruleId, elementId, message) =>
  ({ id: `issue_${++uid}`, severity, ruleId, elementId, message });

function forEachExpression(expr, visit) {
  if (!expr) return;
  visit(expr);
  (expr.contextEntry || []).forEach((e) => forEachExpression(e.value, visit));
  (expr.elements || []).forEach((e) => forEachExpression(e, visit));
  (expr.binding || []).forEach((b) => forEachExpression(b.bindingFormula, visit));
  if (expr.calledFunction) forEachExpression(expr.calledFunction, visit);
  (expr.row || []).forEach((r) => (r.elements || []).forEach((c) => forEachExpression(c, visit)));
  if (expr.body) forEachExpression(expr.body, visit);
}

// ─── rules ─────────────────────────────────────────────────────────────

export function ruleMissingName(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    if (!el.name || !el.name.trim()) {
      out.push(mkIssue('warning', 'missing-name', el.id, `${el.$type.replace('dmn:','')} has no name`));
    }
  });
  return out;
}

export function ruleDuplicateId(ctx) {
  const out = [];
  const seen = new Map();
  const walk = (el) => {
    if (el && el.id) {
      if (seen.has(el.id)) out.push(mkIssue('error', 'duplicate-id', el.id, `Duplicate id '${el.id}'`));
      else seen.set(el.id, el);
    }
  };
  ctx.drgElements.forEach((el) => {
    walk(el);
    (el.informationRequirement || []).forEach(walk);
    (el.knowledgeRequirement || []).forEach(walk);
    (el.authorityRequirement || []).forEach(walk);
    const logic = el.decisionLogic || el.encapsulatedLogic;
    if (logic) forEachExpression(logic, walk);
  });
  return out;
}

export function ruleBrokenReference(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    const reqs = [
      ...(el.informationRequirement || []),
      ...(el.knowledgeRequirement || []),
      ...(el.authorityRequirement || []),
    ];
    reqs.forEach((req) => {
      const refs = ['requiredDecision', 'requiredInput', 'requiredKnowledge', 'requiredAuthority'];
      for (const name of refs) {
        if (!req[name]) continue;
        const href = req[name].href;
        const targetId = (href || '').split('#').pop();
        if (!ctx.byId.has(targetId)) {
          out.push(mkIssue('error', 'broken-reference', req.id || el.id,
            `${req.$type.replace('dmn:','')} on '${el.id}' points to missing '${targetId}'`));
        }
      }
    });
  });
  return out;
}

export function ruleDecisionWithoutLogic(ctx) {
  const out = [];
  ctx.drgElements
    .filter((el) => el.$type === 'dmn:Decision' && !el.decisionLogic)
    .forEach((el) => out.push(mkIssue('warning', 'decision-without-logic', el.id,
      `Decision '${el.name || el.id}' has no decisionLogic`)));
  return out;
}

export function ruleDecisionTableShape(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    const dt = el.decisionLogic;
    if (!dt || dt.$type !== 'dmn:DecisionTable') return;
    if (!(dt.output || []).length) {
      out.push(mkIssue('error', 'decisiontable-no-output', el.id,
        `DecisionTable on '${el.id}' has no output`));
    }
    if (!(dt.rule || []).length) {
      out.push(mkIssue('warning', 'decisiontable-empty', el.id,
        `DecisionTable on '${el.id}' has no rules`));
    }
    (dt.rule || []).forEach((r, i) => {
      const inCount = (r.inputEntry || []).length;
      const wantIn = (dt.input || []).length;
      if (inCount !== wantIn) {
        out.push(mkIssue('error', 'decisiontable-rule-arity', r.id || el.id,
          `Rule ${i + 1} on '${el.id}' has ${inCount} inputEntries, expected ${wantIn}`));
      }
      const outCount = (r.outputEntry || []).length;
      const wantOut = (dt.output || []).length;
      if (outCount !== wantOut) {
        out.push(mkIssue('error', 'decisiontable-rule-arity', r.id || el.id,
          `Rule ${i + 1} on '${el.id}' has ${outCount} outputEntries, expected ${wantOut}`));
      }
    });
  });
  return out;
}

export function ruleInvocationMissingCalled(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    const logic = el.decisionLogic;
    if (!logic) return;
    forEachExpression(logic, (expr) => {
      if (expr.$type === 'dmn:Invocation' && !expr.calledFunction) {
        out.push(mkIssue('error', 'invocation-missing-called', expr.id || el.id,
          `Invocation on '${el.id}' has no calledFunction`));
      }
    });
  });
  return out;
}

export function ruleContextMultipleResults(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    const logic = el.decisionLogic;
    if (!logic) return;
    forEachExpression(logic, (expr) => {
      if (expr.$type !== 'dmn:Context') return;
      const results = (expr.contextEntry || []).filter((e) => !(e.variable && e.variable.name));
      if (results.length > 1) {
        out.push(mkIssue('error', 'context-multiple-results', expr.id || el.id,
          `Context on '${el.id}' has ${results.length} result entries (max 1)`));
      }
    });
  });
  return out;
}

export function ruleFeelParseError(ctx) {
  const out = [];
  const check = (text, elementId, where) => {
    if (!text || !text.trim()) return;
    try { parse(text); }
    catch (err) {
      out.push(mkIssue('error', 'feel-parse-error', elementId,
        `FEEL parse error in ${where}: ${err.message.replace(/^\[feel[^\]]*\]\s*/, '')}`));
    }
  };
  ctx.drgElements.forEach((el) => {
    const logic = el.decisionLogic || el.encapsulatedLogic;
    if (!logic) return;
    forEachExpression(logic, (expr) => {
      if (expr.$type === 'dmn:LiteralExpression') {
        check(expr.text, expr.id || el.id, `literal on '${el.id}'`);
      }
    });
    if (logic.$type === 'dmn:DecisionTable') {
      (logic.output || []).forEach((o, oi) => {
        (logic.rule || []).forEach((r, ri) => {
          const oe = (r.outputEntry || [])[oi];
          if (oe) check(oe.text, oe.id || r.id, `output entry rule ${ri + 1} col ${oi + 1} on '${el.id}'`);
        });
      });
    }
  });
  return out;
}

function _collectFreeIdents(node, locals, out) {
  if (!node || typeof node !== 'object') return;
  switch (node.type) {
    case 'Ident':
      if (!locals.has(node.name) && !BUILTIN_ALIASES[node.name]) out.add(node.name);
      return;
    case 'Path':
      _collectFreeIdents(node.obj, locals, out);
      return;
    case 'Call':
      _collectFreeIdents(node.callee, locals, out);
      (node.args || []).forEach((a) => _collectFreeIdents(a, locals, out));
      // node.named is an object { paramName: exprAst } per the parser, not
      // an array. Walk its values.
      Object.values(node.named || {}).forEach((v) => _collectFreeIdents(v, locals, out));
      return;
    case 'For':
    case 'Quantified': {
      _collectFreeIdents(node.iter, locals, out);
      const inner = new Set(locals); inner.add(node.name);
      _collectFreeIdents(node.body, inner, out);
      return;
    }
    case 'Context': {
      const inner = new Set(locals);
      (node.entries || []).forEach((e) => {
        _collectFreeIdents(e.value, inner, out);
        if (e.key) inner.add(e.key);
      });
      return;
    }
    default:
      for (const k of Object.keys(node)) {
        const v = node[k];
        if (v && typeof v === 'object') {
          if (Array.isArray(v)) v.forEach((x) => _collectFreeIdents(x, locals, out));
          else _collectFreeIdents(v, locals, out);
        }
      }
  }
}

export function extractFreeIdents(feelText) {
  try {
    const ast = parse(feelText);
    const out = new Set();
    _collectFreeIdents(ast, new Set(), out);
    return Array.from(out);
  } catch {
    return [];
  }
}

// FEEL references an upstream element by its variable.name (the canonical
// FEEL identifier). Some authoring tools — and editor-created decisions in
// this project — omit the variable, in which case the element name acts as
// the implicit identifier. Add both so either reference style is recognized.
function _namesConnectedTo(decision, byId) {
  const names = new Set();
  const reqs = [
    ...(decision.informationRequirement || []),
    ...(decision.knowledgeRequirement || []),
  ];
  reqs.forEach((req) => {
    ['requiredDecision', 'requiredInput', 'requiredKnowledge'].forEach((ref) => {
      if (!req[ref]) return;
      const href = req[ref].href;
      const targetId = (href || '').split('#').pop();
      const target = byId.get(targetId);
      if (!target) return;
      const varName = target.variable && target.variable.name;
      if (varName && varName.trim()) names.add(varName.trim());
      if (target.name && target.name.trim()) {
        names.add(target.name.trim());
        // FEEL parser tokenizes "Calcular Riesgo" as two idents; accept the
        // sanitized form so a reference like `CalcularRiesgo` matches an
        // upstream whose display name has spaces.
        const ident = sanitizeFeelIdent(target.name);
        if (ident) names.add(ident);
      }
    });
  });
  return names;
}

export function ruleInputExpressionUnconnected(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    if (el.$type !== 'dmn:Decision') return;
    const dt = el.decisionLogic;
    if (!dt || dt.$type !== 'dmn:DecisionTable') return;
    const connected = _namesConnectedTo(el, ctx.byId);
    (dt.input || []).forEach((inp, i) => {
      const text = inp.inputExpression && inp.inputExpression.text;
      if (!text || !text.trim()) return;
      const idents = extractFreeIdents(text);
      idents.forEach((name) => {
        if (!connected.has(name)) {
          out.push(mkIssue('warning', 'input-not-connected', inp.id || el.id,
            `Columna ${i + 1} de '${el.name || el.id}' usa '${name}' pero no está conectado en el dmn`));
        }
      });
    });
  });
  return out;
}

export function ruleUnaryTestParseError(ctx) {
  const out = [];
  ctx.drgElements.forEach((el) => {
    const dt = el.decisionLogic;
    if (!dt || dt.$type !== 'dmn:DecisionTable') return;
    (dt.rule || []).forEach((r, ri) => {
      (r.inputEntry || []).forEach((ie, ii) => {
        if (!ie || !ie.text || !ie.text.trim()) return;
        try { compileUnaryTests(ie.text); }
        catch (err) {
          out.push(mkIssue('error', 'unary-test-parse-error', ie.id || r.id,
            `Unary test parse error at rule ${ri + 1} col ${ii + 1} on '${el.id}': ${err.message.replace(/^\[feel[^\]]*\]\s*/, '')}`));
        }
      });
    });
  });
  return out;
}

// ─── EPX Machine Learning decision rules (ML01–ML07) ─────────────────────
//
// These rules apply only to decisions whose `decisionLogic.$type` is
// `epx:MachineLearningDecision`. They mirror the table in §4.6 of the plan:
//
//   ML01 modelRef + modelVersion are non-empty                       (error)
//   ML02 runtime ∈ { rest, pmml, onnx }                              (error)
//   ML03 if runtime === 'rest', endpoint is required + URL-shaped    (error)
//   ML04 each inputBindings[].source parses as FEEL                  (error)
//   ML05 at least one outputBinding                                  (error)
//   ML06 confidenceThreshold ∈ [0, 1] when present                   (warning)
//   ML07 fallback is defined (any strategy)                          (warning)

const ML_RUNTIMES = new Set(['rest', 'pmml', 'onnx']);

function isHttpUrl(value) {
  if (!value || typeof value !== 'string') return false;
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function _forEachMlDecision(ctx, visit) {
  ctx.drgElements.forEach((el) => {
    const logic = el.decisionLogic;
    if (!logic || logic.$type !== 'epx:MachineLearningDecision') return;
    visit(el, logic);
  });
}

export function ruleMlModelRequired(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (!logic.modelRef || !String(logic.modelRef).trim() ||
        !logic.modelVersion || !String(logic.modelVersion).trim()) {
      out.push(mkIssue('error', 'ML01', logic.id || el.id,
        `ML decision on '${el.name || el.id}' requires modelRef and modelVersion`));
    }
  });
  return out;
}

export function ruleMlRuntimeKnown(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (!logic.runtime || !ML_RUNTIMES.has(logic.runtime)) {
      out.push(mkIssue('error', 'ML02', logic.id || el.id,
        `ML decision on '${el.name || el.id}' has unsupported runtime '${logic.runtime || ''}' (expected rest|pmml|onnx)`));
    }
  });
  return out;
}

export function ruleMlRestEndpoint(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (logic.runtime !== 'rest') return;
    const ep = logic.endpoint;
    if (!ep || !String(ep).trim()) {
      out.push(mkIssue('error', 'ML03', logic.id || el.id,
        `ML decision on '${el.name || el.id}' uses runtime 'rest' but has no endpoint`));
      return;
    }
    if (!isHttpUrl(ep)) {
      out.push(mkIssue('error', 'ML03', logic.id || el.id,
        `ML decision on '${el.name || el.id}' has invalid endpoint URL '${ep}'`));
    }
  });
  return out;
}

export function ruleMlInputBindingsFeel(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    (logic.inputBindings || []).forEach((b, i) => {
      const src = b.source;
      if (!src || !String(src).trim()) return;
      try { parse(src); }
      catch (err) {
        out.push(mkIssue('error', 'ML04', el.id,
          `ML decision on '${el.name || el.id}': inputBinding[${i}] (feature '${b.feature || ''}') has FEEL parse error: ${err.message.replace(/^\[feel[^\]]*\]\s*/, '')}`));
      }
    });
  });
  return out;
}

export function ruleMlOutputBindingsRequired(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (!(logic.outputBindings || []).length) {
      out.push(mkIssue('error', 'ML05', logic.id || el.id,
        `ML decision on '${el.name || el.id}' must define at least one outputBinding`));
    }
  });
  return out;
}

export function ruleMlConfidenceThresholdRange(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (logic.confidenceThreshold == null) return;
    const v = Number(logic.confidenceThreshold);
    if (!Number.isFinite(v) || v < 0 || v > 1) {
      out.push(mkIssue('warning', 'ML06', logic.id || el.id,
        `ML decision on '${el.name || el.id}': confidenceThreshold ${logic.confidenceThreshold} is outside [0, 1]`));
    }
  });
  return out;
}

export function ruleMlFallbackPresent(ctx) {
  const out = [];
  _forEachMlDecision(ctx, (el, logic) => {
    if (!logic.fallback || !logic.fallback.strategy) {
      out.push(mkIssue('warning', 'ML07', logic.id || el.id,
        `ML decision on '${el.name || el.id}' has no fallback configured (recommend value/rule/error)`));
    }
  });
  return out;
}

export const DEFAULT_RULES = [
  ruleMissingName,
  ruleDuplicateId,
  ruleBrokenReference,
  ruleDecisionWithoutLogic,
  ruleDecisionTableShape,
  ruleInvocationMissingCalled,
  ruleContextMultipleResults,
  ruleFeelParseError,
  ruleUnaryTestParseError,
  ruleInputExpressionUnconnected,
  // EPX ML extension rules (ML01–ML07)
  ruleMlModelRequired,
  ruleMlRuntimeKnown,
  ruleMlRestEndpoint,
  ruleMlInputBindingsFeel,
  ruleMlOutputBindingsRequired,
  ruleMlConfidenceThresholdRange,
  ruleMlFallbackPresent,
];
